import {
  faClockRotateLeft,
  faRotateBack,
  faTrash,
  faUnlock,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Button, Col, Input, Row, UncontrolledTooltip } from "reactstrap";
import routeNames from "../../router/routeNames";
import { getProjects } from "../../services/projectService";
import { deleteUser, getUsers, unlockUser } from "../../services/userService";
import Message from "../../utility/notifications/Message";
import Notify from "../../utility/notifications/Notify";
import { isHostUser } from "../../utility/Utils";
import PageTitle from "../components/Common/PageTitle";
import { getRolesForDropdown } from "./../../services/userService";
import { TenantContext } from "../../context/tenantContext";
import { sendPasswordResetCode } from "../../services/accountService";
import FormCheckInput from "react-bootstrap/esm/FormCheckInput";
import AppButton from "../components/Common/AppButton";
import useJwt from "../../services/useJwt";
import { Tooltip } from "react-bootstrap";

const Users = () => {
  const { tenant } = useContext(TenantContext);

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isActiveFilter, setIsActiveFilter] = useState(false);
  const [roleFilter, setRoleFilter] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    async function fetchUsers(projectIds, roleIds) {
      const response = await getUsers(
        projectIds,
        roleIds,
        null,
        isActiveFilter
      );
      setUsers(response.data.result.items);
    }

    async function fetchRoles() {
      const roles = await getRolesForDropdown();
      if (roles) setRoles(roles.data.result.items);
    }

    async function fetchProjects() {
      const projects = await getProjects();

      let options = projects.data.result.items.map(function (project) {
        return { value: project.id, label: project.name };
      });

      if (options) setProjects(options);
    }

    fetchUsers();
    fetchRoles();
    fetchProjects();
  }, []);

  async function handleChangeRole(value) {
    const roleIds = value.map((v) => v.value);
    setRoleFilter(roleIds);

    const response = await getUsers(
      projectFilter,
      roleIds,
      searchTerm,
      isActiveFilter
    );
    setUsers(response.data.result.items);
  }

  async function handleProjectChange(value) {
    const projectIds = value.map((v) => v.value);
    setProjectFilter(projectIds);

    const response = await getUsers(
      projectIds,
      roleFilter,
      searchTerm,
      isActiveFilter
    );
    setUsers(response.data.result.items);
  }

  async function handleIsActiveChange(event) {
    setIsActiveFilter(event);

    const response = await getUsers(
      projectFilter,
      roleFilter,
      searchTerm,
      event
    );
    setUsers(response.data.result.items);
  }

  async function handleChange(event) {
    setSearchTerm(event.target.value);

    const response = await getUsers(
      projectFilter,
      roleFilter,
      searchTerm,
      isActiveFilter
    );
    setUsers(response.data.result.items);
  }

  const handleDelete = async (user) => {
    Message(
      "Are you sure?",
      `Do you want to delete user ${user.fullName}?`,
      "warning"
    ).then(async (result) => {
      if (result.isConfirmed) {
        await deleteUser(user.id)
          .then(async (res) => {
            if (res.status === 200) {
              Notify.success(`User ${user.fullName} has been deleted!`);
              const response = await getUsers();
              setUsers(response.data.result.items);
            }
          })
          .catch((err) => {
            if (err.response) {
              Message(
                err.response.data.error.message,
                err.response.data.error.details,
                "error"
              );
            }
          });
      }
    });
  };

  const handleResetPassword = async (user) => {
    Message(
      "Are you sure?",
      `Do you want to send a reset password email to ${user.fullName}?`,
      "warning"
    ).then(async (result) => {
      if (result.isConfirmed) {
        await sendPasswordResetCode({
          emailAddress: user.emailAddress,
          tenantName: tenant.tenancyName,
        })
          .then(async (res) => {
            if (res.status === 200) {
              Notify.success(
                `A password reset mail has been sent to ${user.fullName}!`
              );
            }
          })
          .catch((err) => {
            if (err.response) {
              Message(
                err.response.data.error.message,
                err.response.data.error.details,
                "error"
              );
            }
          });
      }
    });
  };

  async function handleResetAuthenticator(user) {
    const messageResponse = await Message(
      "Multi factor authenticator reset",
      `Are you sure you want to reset the Multi factor authenticator of ${user.fullName}?`,
      "warning"
    );

    if (messageResponse.isConfirmed) {
      await useJwt
        .resetTwoFactorAuthenticator({
          userId: user.id,
        })
        .then(async (res) => {
          if (res.status === 200) {
            Notify.success(
              `Multi factor authenticator has been reset for ${user.fullName}!`
            );
            const response = await getUsers();
            setUsers(response.data.result.items);
          }
        });
    }
  }

  async function handleUnlockUser(user) {
    Message(
      "Are you sure?",
      `Do you want to unlock ${user.fullName}?`,
      "warning"
    ).then(async (result) => {
      if (result.isConfirmed) {
        await unlockUser(user.id)
          .then(async (res) => {
            if (res.status === 200) {
              Notify.success(`${user.fullName}'s account is unlocked!`);

              const response = await getUsers();
              setUsers(response.data.result.items);
            }
          })
          .catch((err) => {
            if (err.response) {
              Message(
                err.response.data.error.message,
                err.response.data.error.details,
                "error"
              );
            }
          });
      }
    });
  }

  return (
    <div>
      <>
        <PageTitle title="User overview" />
        {users && (
          <>
            <Row className="pb-1">
              <Col xs={8} className="d-flex justify-content-end mb-2">
                <Input
                  placeholder="Search for user..."
                  className="me-2"
                  onChange={(event) => handleChange(event)}
                />
              </Col>
              <Col className="mt-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="dataEntryCorrect"
                  onClick={(e) => handleIsActiveChange(e.target.checked)}
                />
                <label
                  htmlFor="dataEntryCorrect"
                  className="form-check-label ms-2"
                >
                  Show Inactive Records
                </label>
              </Col>
            </Row>
            <Row className="pb-3">
              <Col lg={6} sm={6}>
                <h6>Rollen</h6>
                <Select isMulti options={roles} onChange={handleChangeRole} />
              </Col>
              {!isHostUser() && (
                <Col lg={6} sm={6}>
                  <h6>Projecten</h6>
                  <Select
                    isMulti
                    options={projects}
                    onChange={handleProjectChange}
                  />
                </Col>
              )}
            </Row>

            <Row>
              <DataTable
                pagination
                responsive
                columns={[
                  {
                    name: "Name",
                    selector: (row) => row.fullName,
                    sortable: true,
                    cell: (row) => (
                      <Link to={`/users/edit/${row.id}`}>{row.fullName}</Link>
                    ),
                  },
                  {
                    name: "Is active",
                    sortable: true,
                    cell: (row) => (row.isActive ? "Yes" : "No"),
                  },
                  {
                    name: "Emailaddress",
                    sortable: true,
                    cell: (row) => row.emailAddress,
                  },
                  {
                    name: "Roles",
                    sortable: true,
                    cell: (row) => row.roleNames.join(", "),
                  },
                  {
                    name: "Actions",
                    cell: (row) => (
                      <>
                        <Button
                          onClick={() => handleDelete(row)}
                          size="sm"
                          color="danger"
                        >
                          <FontAwesomeIcon icon={faTrash} size="xs" />
                        </Button>

                        {row?.isLocked && (
                          <div className="ms-2">
                            <Button
                              onClick={() => handleUnlockUser(row)}
                              size="sm"
                              color="primary"
                            >
                              <FontAwesomeIcon icon={faUnlock} size="xs" />
                            </Button>
                          </div>
                        )}

                        {
                            !row?.isOfficeUser && <div className="ms-2">
                            <Button
                                onClick={() => handleResetPassword(row)}
                                size="sm"
                                color="warning"
                            >
                              <FontAwesomeIcon icon={faRotateBack}/>
                            </Button>
                          </div>
                        }

                        {row?.hasActiveTwoFactorAuthenticator && (
                          <div className="ms-2">
                            <Button
                              id={`reset-authenticator-btn-${row.id}`}
                              onClick={() => handleResetAuthenticator(row)}
                              size="sm"
                              color="secondary"
                            >
                              <FontAwesomeIcon icon={faClockRotateLeft} />
                            </Button>
                            <UncontrolledTooltip
                              placement="top"
                              target={`reset-authenticator-btn-${row.id}`}
                            >
                              Reset two-factor authenticator
                            </UncontrolledTooltip>
                          </div>
                        )}
                      </>
                    ),
                  },
                ]}
                data={users}
              />
            </Row>
          </>
        )}

        <Row>
          <Col>
            <Link to={routeNames.user_create}>
              <Button type="button" color="primary" size="lg">
                Create new user
              </Button>
            </Link>
          </Col>
        </Row>
      </>
    </div>
  );
};

export default Users;
